import React, { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { ThemeProvider } from "@/hooks/useThemeContext";
import { EnhancedThemeProvider } from "@/hooks/useEnhancedTheme";

import { NavigationProvider } from "@/contexts/NavigationContext";
import { ToastProvider } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/sonner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy load non-essential providers
const LazyAnalyticsProvider = lazy(() => 
  import("@/contexts/AnalyticsContext").then(module => ({
    default: module.AnalyticsProvider
  }))
);

interface AppProvidersProps {
  children: React.ReactNode;
  authRequired?: boolean;
}

export const AppProviders: React.FC<AppProvidersProps> = ({ 
  children, 
  authRequired = false 
}) => {
  return (
    <QueryClientProvider client={queryClient}>
      <NextThemesProvider defaultTheme="dark" forcedTheme="dark" storageKey="ui-theme">
        <ThemeProvider>
          <EnhancedThemeProvider>
            <NavigationProvider>
              <ToastProvider>
                <Suspense fallback={
                  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
                    <div className="flex flex-col items-center space-y-4">
                      <div className="h-12 w-12 border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                      <p className="text-slate-400">Loading EquiForge...</p>
                    </div>
                  </div>
                }>
                  <LazyAnalyticsProvider>
                    {children}
                    <Toaster />
                    {import.meta.env.DEV && <ReactQueryDevtools />}
                  </LazyAnalyticsProvider>
                </Suspense>
              </ToastProvider>
            </NavigationProvider>
          </EnhancedThemeProvider>
        </ThemeProvider>
      </NextThemesProvider>
    </QueryClientProvider>
  );
};
